import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import jwt_decode from 'jwt-decode';

const UserContext = createContext();

function readStoredUser() {
    if (typeof window === 'undefined' || !window.sessionStorage) return null;

    try {
        const user = JSON.parse(window.sessionStorage.getItem('user'));
        if (!user || typeof user.token !== 'string' || !user.token.trim()) return null;

        const data = jwt_decode(user.token);
        if (typeof data.exp === 'number' && data.exp <= Date.now() / 1000) {
            window.sessionStorage.removeItem('user');
            return null;
        }
        return { token: user.token, data };
    } catch (error) {
        window.sessionStorage.removeItem('user');
        return null;
    }
}

export const UserProvider = ({ children }) => {
    const [storedUser] = useState(readStoredUser);
    const [token, setToken] = useState(storedUser?.token ?? null);
    const [userData, setUserData] = useState(storedUser?.data ?? null);

    const roles = useMemo(() => {
        if (typeof userData?.role !== 'string') return [];

        return userData.role
            .trim()
            .toLowerCase()
            .split(/\s+/)
            .filter(Boolean);
    }, [userData?.role]);

    const hasRoles = useCallback((requiredRoles) => {
        if (!Array.isArray(requiredRoles)) return false;
        return requiredRoles.every(role =>
            typeof role === 'string' && roles.includes(role.toLowerCase())
        );
    }, [roles]);

    const hasAnyRole = useCallback((requiredRoles) => {
        if (!Array.isArray(requiredRoles) || requiredRoles.length === 0) return false;
        return requiredRoles.some(role =>
            typeof role === 'string' && roles.includes(role.toLowerCase())
        );
    }, [roles]);

    const setUser = useCallback((token, data) => {
        setToken(token);
        setUserData(data);
    }, []);

    const user = useMemo(() => ({
        token,
        data: userData,
        roles,
        hasRoles,
        hasAnyRole,
    }), [token, userData, roles, hasRoles, hasAnyRole]);

    const value = useMemo(() => ({ user, setUser }), [user, setUser]);

    return (
        <UserContext.Provider value={value}>
            {children}
        </UserContext.Provider>
    );
};

export const useUser = () => {
    const context = useContext(UserContext);
    if (context === undefined) {
        throw new Error('useUser must be used within a UserProvider');
    }
    return context;
};
